import React, { useMemo, useState } from "react";

import { ArrowLeft, ChevronRight, Shapes } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { H2 } from "~/components/ui/typography";
import { FeedbackSection } from "~/features/practitioner-feedback/components/feedback";
import { cn } from "~/lib/utils";
import type { IdentifiedPart, Part } from "~/types/global";

// AIDEV-NOTE: Client-safe mirror of the shapes stored at
// rawReport.modelComparisons.deepseekV4Pro (see session-deepseek-shadow.ts +
// part-match-comparison.ts). Kept local so we don't pull server modules into the bundle.
export interface PartMatchRow {
    labeledPartName: string;
    predictedPartName: string;
    labeledEvidenceLevel: string;
    predictedEvidenceLevel: string;
    labeledPresence: string;
    predictedPresence: string;
    labeledType: string;
    predictedType: string;
    matchType: "Strict" | "Lenient" | "N/A" | "-";
}

interface PartMatchComparison {
    rawOutput: string;
    rows: PartMatchRow[];
}

interface ShadowMatchPayload {
    status: "completed" | "error" | "waiting_for_outputs";
    identification?: PartMatchComparison;
    detail?: PartMatchComparison;
}

export interface ShadowParts {
    present: Part[];
    other: IdentifiedPart[];
}

export interface DeepseekShadowComparison {
    provider?: string;
    model?: string;
    status?: string;
    summary?: {
        title: string;
        shortSummary: string;
        longSummary: string;
    };
    parts?: ShadowParts;
    matches?: ShadowMatchPayload;
}

const SONNET_LABEL = "Main pipeline";
const DEEPSEEK_LABEL = "DeepSeek";

const formatShadowSummary = (value?: string) =>
    value?.replace(/\|/g, "\n\n").trim();

const isMissing = (value?: string) =>
    !value || value === "-" || value.toLowerCase() === "n/a";

const findPresentPart = (
    parts: ShadowParts | undefined,
    name: string,
): Part | undefined => {
    if (isMissing(name)) return undefined;
    const target = name.trim().toLowerCase();
    return parts?.present.find(
        (part) => part.name.trim().toLowerCase() === target,
    );
};

const matchBadge = (
    matchType: PartMatchRow["matchType"],
): { label: string; variant: "forest" | "warning" | "secondary" } => {
    if (matchType === "Strict")
        return { label: "Strict match", variant: "forest" };
    if (matchType === "Lenient")
        return { label: "Lenient match", variant: "warning" };
    return { label: "No match", variant: "secondary" };
};

const Field: React.FC<{ label: string; value?: string }> = ({
    label,
    value,
}) => (
    <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-normal text-brand-forest-700/60">
            {label}
        </p>
        {value && !isMissing(value) ? (
            <p className="whitespace-pre-wrap text-sm leading-6 text-brand-forest-900">
                {value}
            </p>
        ) : (
            <p className="text-sm italic text-brand-forest-800/40">—</p>
        )}
    </div>
);

const PartColumn: React.FC<{
    label: string;
    name: string;
    part?: Part;
    fallbackType?: string;
    fallbackEvidence?: string;
    fallbackPresence?: string;
    attributeNames: string[];
}> = ({
    label,
    name,
    part,
    fallbackType,
    fallbackEvidence,
    fallbackPresence,
    attributeNames,
}) => {
    const present = !isMissing(name);
    const attrContent = (attr: string) =>
        part?.attributes?.find((a) => a.name === attr)?.content;

    return (
        <div className="min-w-0 space-y-4 border border-brand-forest-100 bg-white p-4">
            <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-normal text-brand-forest-700/60">
                    {label}
                </p>
                {present ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-base font-semibold text-brand-forest-900">
                            {name}
                        </h4>
                        {!part ? (
                            <Badge
                                variant="secondary"
                                className="shrink-0 text-[10px] font-medium"
                            >
                                Identified only
                            </Badge>
                        ) : null}
                    </div>
                ) : (
                    <Skeleton className="h-5 w-32" />
                )}
            </div>

            {present ? (
                <>
                    <div className="grid grid-cols-3 gap-2">
                        <Field
                            label="Type"
                            value={part?.type ?? fallbackType}
                        />
                        <Field
                            label="Confidence"
                            value={part?.evidenceLevel ?? fallbackEvidence}
                        />
                        <Field
                            label="Presence"
                            value={part?.presenceLevel ?? fallbackPresence}
                        />
                    </div>
                    {part ? (
                        <>
                            <Field
                                label="Description"
                                value={part.description}
                            />
                            {part.recap ? (
                                <Field label="Recap" value={part.recap} />
                            ) : null}
                            {part.quote ? (
                                <Field label="Quote" value={part.quote} />
                            ) : null}
                            {attributeNames.map((attr) => (
                                <Field
                                    key={attr}
                                    label={attr}
                                    value={attrContent(attr)}
                                />
                            ))}
                        </>
                    ) : (
                        // AIDEV-NOTE: Matched on the table but no full Part object
                        // (e.g. an "other"/identified-only part). Attributes unavailable.
                        <p className="text-sm italic text-brand-forest-800/40">
                            No detailed attributes captured for this part.
                        </p>
                    )}
                </>
            ) : (
                <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                    <p className="text-sm italic text-brand-forest-800/40">
                        Not identified by {label.toLowerCase()}.
                    </p>
                </div>
            )}
        </div>
    );
};

const PartComparisonDetail: React.FC<{
    row: PartMatchRow;
    sonnetParts?: ShadowParts;
    deepseekParts?: ShadowParts;
}> = ({ row, sonnetParts, deepseekParts }) => {
    const leftPart = findPresentPart(sonnetParts, row.labeledPartName);
    const rightPart = findPresentPart(deepseekParts, row.predictedPartName);

    const attributeNames = useMemo(() => {
        const names: string[] = [];
        const seen = new Set<string>();
        for (const attr of [
            ...(leftPart?.attributes ?? []),
            ...(rightPart?.attributes ?? []),
        ]) {
            if (attr?.name && !seen.has(attr.name)) {
                seen.add(attr.name);
                names.push(attr.name);
            }
        }
        return names;
    }, [leftPart, rightPart]);

    return (
        <div className="grid gap-4 @lg:grid-cols-2">
            <PartColumn
                label={SONNET_LABEL}
                name={row.labeledPartName}
                part={leftPart}
                fallbackType={row.labeledType}
                fallbackEvidence={row.labeledEvidenceLevel}
                fallbackPresence={row.labeledPresence}
                attributeNames={attributeNames}
            />
            <PartColumn
                label={DEEPSEEK_LABEL}
                name={row.predictedPartName}
                part={rightPart}
                fallbackType={row.predictedType}
                fallbackEvidence={row.predictedEvidenceLevel}
                fallbackPresence={row.predictedPresence}
                attributeNames={attributeNames}
            />
        </div>
    );
};

const PartRowSide: React.FC<{
    name: string;
    type: string;
    confidence: string;
    presence: string;
    detailed: boolean;
}> = ({ name, type, confidence, presence, detailed }) => {
    if (isMissing(name)) {
        return (
            <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-16" />
            </div>
        );
    }
    const meta = [
        !isMissing(type) ? type : null,
        !isMissing(confidence) ? `Confidence: ${confidence}` : null,
        !isMissing(presence) ? `Presence: ${presence}` : null,
    ].filter(Boolean);
    return (
        <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
                <p className="truncate text-sm font-semibold text-brand-forest-900">
                    {name}
                </p>
                {!detailed ? (
                    <Badge
                        variant="secondary"
                        className="shrink-0 text-[10px] font-medium"
                    >
                        Identified only
                    </Badge>
                ) : null}
            </div>
            {meta.length > 0 ? (
                <p className="truncate text-xs text-brand-forest-700/70">
                    {meta.join(" · ")}
                </p>
            ) : null}
        </div>
    );
};

const PartsTab: React.FC<{
    matches?: ShadowMatchPayload;
    sonnetParts?: ShadowParts;
    deepseekParts?: ShadowParts;
}> = ({ matches, sonnetParts, deepseekParts }) => {
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const rows = useMemo(
        () =>
            (matches?.detail?.rows ?? []).filter(
                (row) =>
                    !(
                        isMissing(row.labeledPartName) &&
                        isMissing(row.predictedPartName)
                    ) &&
                    row.labeledPartName !== "Self" &&
                    row.predictedPartName !== "Self",
            ),
        [matches],
    );

    if (matches?.status !== "completed" || rows.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-brand-forest-100 bg-white/60 p-8 text-center">
                <p className="text-sm text-brand-forest-700/70">
                    {matches?.status === "error"
                        ? "The part comparison failed to generate."
                        : "Part comparison is still being generated."}
                </p>
            </div>
        );
    }

    if (selectedIndex !== null && rows[selectedIndex]) {
        const row = rows[selectedIndex];
        return (
            <div className="space-y-4">
                <Button
                    variant="ghost"
                    className="flex items-center gap-2 p-0 text-sm text-brand-forest-700 hover:bg-transparent"
                    onClick={() => setSelectedIndex(null)}
                >
                    <ArrowLeft className="size-4" />
                    Back to all parts
                </Button>
                <PartComparisonDetail
                    row={row}
                    sonnetParts={sonnetParts}
                    deepseekParts={deepseekParts}
                />
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3 px-1 text-xs font-semibold uppercase tracking-normal text-brand-forest-700/60">
                <span className="flex-1">{SONNET_LABEL}</span>
                <span className="flex-1">{DEEPSEEK_LABEL}</span>
                <span className="w-24 shrink-0" />
            </div>
            {rows.map((row, index) => {
                const badge = matchBadge(row.matchType);
                const leftDetailed = Boolean(
                    findPresentPart(sonnetParts, row.labeledPartName),
                );
                const rightDetailed = Boolean(
                    findPresentPart(deepseekParts, row.predictedPartName),
                );
                return (
                    <button
                        key={`${row.labeledPartName}-${row.predictedPartName}-${index}`}
                        type="button"
                        onClick={() => setSelectedIndex(index)}
                        className="flex w-full items-center gap-3 rounded-lg border border-brand-forest-100 bg-white p-3 text-left transition-colors hover:border-brand-forest-300"
                    >
                        <PartRowSide
                            name={row.labeledPartName}
                            type={row.labeledType}
                            confidence={row.labeledEvidenceLevel}
                            presence={row.labeledPresence}
                            detailed={leftDetailed}
                        />
                        <PartRowSide
                            name={row.predictedPartName}
                            type={row.predictedType}
                            confidence={row.predictedEvidenceLevel}
                            presence={row.predictedPresence}
                            detailed={rightDetailed}
                        />
                        <div className="flex w-24 shrink-0 items-center justify-end gap-1">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                            <ChevronRight className="size-4 shrink-0 text-brand-forest-400" />
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

const SummariesTab: React.FC<{
    sessionId: string;
    sonnetShortSummary?: string;
    sonnetLongSummary?: string;
    comparison: DeepseekShadowComparison;
}> = ({ sessionId, sonnetShortSummary, sonnetLongSummary, comparison }) => {
    const columns = [
        {
            label: SONNET_LABEL,
            shortSummary: sonnetShortSummary,
            longSummary: sonnetLongSummary,
            isDeepseek: false,
        },
        {
            label: DEEPSEEK_LABEL,
            shortSummary: formatShadowSummary(comparison.summary?.shortSummary),
            longSummary: formatShadowSummary(comparison.summary?.longSummary),
            isDeepseek: true,
        },
    ];

    return (
        <div className="grid gap-4 @lg:grid-cols-2">
            {columns.map((column) => (
                <div
                    key={column.label}
                    className="space-y-4 border border-brand-forest-100 bg-white p-4"
                >
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-brand-forest-800">
                            {column.label}
                        </h3>
                        {column.isDeepseek && (
                            <FeedbackSection
                                sessionId={sessionId}
                                text="Rate DeepSeek summary quality"
                                feature="deepseek_quality"
                                compact
                                metadata={{
                                    actionType: "deepseek_quality",
                                    provider: "deepseek",
                                    surface: "session_summary_comparison",
                                    model: comparison.model,
                                }}
                                feedbackFormKey="deepseek_quality"
                            />
                        )}
                    </div>
                    <Field label="Short summary" value={column.shortSummary} />
                    <Field label="Long summary" value={column.longSummary} />
                </div>
            ))}
        </div>
    );
};

export const ModelComparisonButton: React.FC<{
    sessionId: string;
    sonnetShortSummary?: string;
    sonnetLongSummary?: string;
    sonnetParts?: ShadowParts;
    comparison?: DeepseekShadowComparison;
}> = ({
    sessionId,
    sonnetShortSummary,
    sonnetLongSummary,
    sonnetParts,
    comparison,
}) => {
    const hasSummary = Boolean(comparison?.summary);
    const partsReady = comparison?.matches?.status === "completed";

    if (comparison?.status !== "completed" || (!hasSummary && !partsReady)) {
        return null;
    }

    return (
        <section className="mt-8 border-t border-brand-forest-100 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <H2 className="text-lg">Model comparison</H2>
                    <p className="text-sm text-brand-forest-700/70">
                        Compare the main pipeline with the{" "}
                        {comparison.provider || "DeepSeek"} shadow run.
                    </p>
                </div>
                <Dialog analyticsId="ModelComparisonModal">
                    <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2">
                            <Shapes className="size-4" />
                            Compare models
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="@container max-w-none sm:w-[calc(100vw-2rem)]">
                        <DialogHeader>
                            <DialogTitle>Model comparison</DialogTitle>
                        </DialogHeader>
                        <Tabs defaultValue="summaries" className="mt-2">
                            <TabsList>
                                <TabsTrigger value="summaries">
                                    Summaries
                                </TabsTrigger>
                                <TabsTrigger value="parts">Parts</TabsTrigger>
                            </TabsList>
                            <TabsContent value="summaries">
                                <SummariesTab
                                    sessionId={sessionId}
                                    sonnetShortSummary={sonnetShortSummary}
                                    sonnetLongSummary={sonnetLongSummary}
                                    comparison={comparison}
                                />
                            </TabsContent>
                            <TabsContent value="parts">
                                <PartsTab
                                    matches={comparison.matches}
                                    sonnetParts={sonnetParts}
                                    deepseekParts={comparison.parts}
                                />
                            </TabsContent>
                        </Tabs>
                    </DialogContent>
                </Dialog>
            </div>
        </section>
    );
};
